import { useMemo } from 'react';

import { useResolvedImageUrl } from '../../../../hooks/useResolvedImageUrl';
import { useVariableContext } from '../../../../lib/template-variables';
import type { SignatureInfo } from '../../../appointments/fhirNoteMapping';
import { useDocumentationSettings } from '../hooks';
import {
  resolveDocumentFooterText,
  shouldShowPracticeLogo,
  shouldUsePreLicensedDocumentFormat,
} from '../utils/documentFormat';

export interface UseDocumentFormatPresentationOptions {
  /** When true, the note author requires supervisor cosignature (pre-licensed format). */
  requiresCosignature?: boolean;
  /** @deprecated Use `requiresCosignature`. */
  hasSupervisor?: boolean;
  /** Task assigner — resolves supervisor footer variables on client portal. */
  assignerPractitionerId?: string;
  /** Signed supervisor signature — overrides loaded supervisor context when present. */
  supervisorSignature?: SignatureInfo;
}

export interface DocumentFormatPresentation {
  showLogo: boolean;
  logoUrl?: string;
  footerText?: string;
  addSupervisorInfo: boolean;
  usePreLicensedFormat: boolean;
}

export function useDocumentFormatPresentation(
  options: UseDocumentFormatPresentationOptions = {},
): DocumentFormatPresentation {
  const { settings } = useDocumentationSettings();
  const { context } = useVariableContext({
    assignerPractitionerId: options.assignerPractitionerId,
  });
  const requiresCosignature = options.requiresCosignature ?? options.hasSupervisor ?? false;
  const usePreLicensedFormat = shouldUsePreLicensedDocumentFormat(settings, requiresCosignature);
  const showLogo = shouldShowPracticeLogo(settings);

  const enrichedContext = useMemo(() => {
    if (!options.supervisorSignature) return context;
    return {
      ...context,
      supervisor: {
        ...context.supervisor,
        name: options.supervisorSignature.name || context.supervisor?.name,
        fullName: options.supervisorSignature.name || context.supervisor?.fullName,
        licenseNumber: options.supervisorSignature.license || context.supervisor?.licenseNumber,
        npiNumber: context.supervisor?.npiNumber,
      },
    };
  }, [context, options.supervisorSignature]);

  const footerText = resolveDocumentFooterText(settings, enrichedContext, {
    usePreLicensedFormat,
    onUnresolved: 'placeholder',
  });

  const rawLogoUrl = showLogo ? context.organization?.logoUrl : undefined;
  const logoUrl = useResolvedImageUrl(rawLogoUrl);

  return {
    showLogo,
    logoUrl,
    footerText,
    addSupervisorInfo: settings.documentFormat.preLicensedFormat.addSupervisorInfo,
    usePreLicensedFormat,
  };
}
